import * as THREE from 'three';
import { CONFIG } from '../app/config.js';

export const ObstacleType = {
  BLOCKER: 'BLOCKER',
  HURDLE: 'HURDLE',
  ORB: 'ORB',
};

export class ObstacleManager {
  constructor(scene) {
    this.scene = scene;
    this.activeItems = [];
    this.pool = {
      [ObstacleType.BLOCKER]: [],
      [ObstacleType.HURDLE]: [],
      [ObstacleType.ORB]: [],
    };

    this.spawnTimer = 0;
    this.nextSpawnInterval = 1.4;

    // Shared Geometries (allocated once)
    this.blockerGeo = new THREE.BoxGeometry(1.9, 1.8, 0.6);
    this.hurdleGeo = new THREE.BoxGeometry(1.9, 0.55, 0.35);
    this.orbGeo = new THREE.SphereGeometry(0.35, 10, 8);
    this.orbRingGeo = new THREE.TorusGeometry(0.48, 0.05, 4, 12);

    // Shared Materials
    this.blockerMaterial = new THREE.MeshStandardMaterial({
      color: 0xff657a,
      roughness: 0.4,
      metalness: 0.3,
    });

    this.hurdleMaterial = new THREE.MeshStandardMaterial({
      color: 0xffd166,
      roughness: 0.3,
      metalness: 0.4,
    });

    this.orbMaterial = new THREE.MeshStandardMaterial({
      color: 0xd9ff66,
      emissive: 0x88cc00,
      emissiveIntensity: 0.6,
      roughness: 0.2,
      metalness: 0.1,
    });

    this.orbRingMaterial = new THREE.MeshBasicMaterial({
      color: 0x42e8e0,
    });

    // Pre-populate pool
    this.prepopulatePool(ObstacleType.BLOCKER, 12);
    this.prepopulatePool(ObstacleType.HURDLE, 12);
    this.prepopulatePool(ObstacleType.ORB, 20);
  }

  prepopulatePool(type, count) {
    for (let i = 0; i < count; i++) {
      const mesh = this.createMeshForType(type);
      mesh.visible = false;
      this.scene.add(mesh);
      this.pool[type].push(mesh);
    }
  }

  createMeshForType(type) {
    let group = new THREE.Group();
    group.userData = {
      type,
      active: false,
      collected: false,
      laneIndex: 1,
      // Pre-calculated bounding box dimensions
      boxHalfWidth: 0.95,
      boxHeight: 1.8,
      boxHalfDepth: 0.3,
    };

    if (type === ObstacleType.BLOCKER) {
      const mesh = new THREE.Mesh(this.blockerGeo, this.blockerMaterial);
      mesh.position.y = 0.9;
      group.add(mesh);
      group.userData.boxHeight = 1.8;
    } else if (type === ObstacleType.HURDLE) {
      const mesh = new THREE.Mesh(this.hurdleGeo, this.hurdleMaterial);
      mesh.position.y = 0.275;
      group.add(mesh);
      group.userData.boxHeight = 0.6;
    } else if (type === ObstacleType.ORB) {
      const orb = new THREE.Mesh(this.orbGeo, this.orbMaterial);
      orb.position.y = 0.8;
      group.add(orb);

      const ring = new THREE.Mesh(this.orbRingGeo, this.orbRingMaterial);
      ring.position.y = 0.8;
      ring.rotation.x = Math.PI / 3;
      group.add(ring);

      group.userData.boxHalfWidth = 0.45;
      group.userData.boxHeight = 1.2;
      group.userData.boxHalfDepth = 0.45;
    }

    return group;
  }

  acquireFromPool(type) {
    let item = this.pool[type].pop();
    if (!item) {
      item = this.createMeshForType(type);
      this.scene.add(item);
    }
    item.visible = true;
    item.scale.set(1, 1, 1);
    item.userData.active = true;
    item.userData.collected = false;
    item.userData.isAbsorbing = false;
    item.userData.absorbProgress = 0;
    item.userData.lastZ = item.position.z;
    return item;
  }

  releaseToPool(item) {
    item.visible = false;
    item.scale.set(1, 1, 1);
    item.userData.active = false;
    item.userData.collected = false;
    item.userData.isAbsorbing = false;
    item.userData.absorbProgress = 0;
    this.pool[item.userData.type].push(item);
  }

  reset() {
    for (let i = 0; i < this.activeItems.length; i++) {
      this.releaseToPool(this.activeItems[i]);
    }
    this.activeItems.length = 0;
    this.spawnTimer = 0;
    this.nextSpawnInterval = 1.2;
  }

  spawnWave() {
    const lanes = [0, 1, 2];
    const spawnZ = CONFIG.WORLD.SPAWN_Z;

    // Fairness rule: randomize obstacle types while leaving at least one safe/jumpable lane
    const safeLane = lanes[Math.floor(Math.random() * lanes.length)];

    for (let l = 0; l < lanes.length; l++) {
      const laneIndex = lanes[l];
      const laneX = CONFIG.LANES.COORDS[laneIndex];

      if (laneIndex === safeLane) {
        // Spawn collectible energy orb in safe lane
        if (Math.random() > 0.3) {
          const orb = this.acquireFromPool(ObstacleType.ORB);
          orb.position.set(laneX, 0, spawnZ);
          orb.userData.laneIndex = laneIndex;
          this.activeItems.push(orb);
        }
      } else {
        // In other lanes, choose between Blocker, Hurdle, or empty
        const rand = Math.random();
        if (rand < 0.45) {
          // Blocker (Full barrier)
          const blocker = this.acquireFromPool(ObstacleType.BLOCKER);
          blocker.position.set(laneX, 0, spawnZ);
          blocker.userData.laneIndex = laneIndex;
          this.activeItems.push(blocker);
        } else if (rand < 0.85) {
          // Hurdle (Low jumpable)
          const hurdle = this.acquireFromPool(ObstacleType.HURDLE);
          hurdle.position.set(laneX, 0, spawnZ);
          hurdle.userData.laneIndex = laneIndex;
          this.activeItems.push(hurdle);

          // Add floating orb above hurdle as jump incentive
          if (Math.random() > 0.4) {
            const highOrb = this.acquireFromPool(ObstacleType.ORB);
            highOrb.position.set(laneX, 1.4, spawnZ);
            highOrb.userData.laneIndex = laneIndex;
            this.activeItems.push(highOrb);
          }
        }
      }
    }
  }

  update(worldSpeed, dt, runnerPos = null) {
    // 1. Spawning timer
    this.spawnTimer += dt;
    if (this.spawnTimer >= this.nextSpawnInterval) {
      this.spawnTimer = 0;
      this.spawnWave();
      // Cadence scales dynamically with speed
      const speedRatio = (worldSpeed - CONFIG.WORLD.INITIAL_SPEED) / (CONFIG.WORLD.MAX_SPEED - CONFIG.WORLD.INITIAL_SPEED);
      this.nextSpawnInterval = CONFIG.WORLD.MAX_SPAWN_INTERVAL - speedRatio * (CONFIG.WORLD.MAX_SPAWN_INTERVAL - CONFIG.WORLD.MIN_SPAWN_INTERVAL);
    }

    // 2. Move active items toward camera along +Z and handle absorption
    const dz = worldSpeed * dt;
    for (let i = this.activeItems.length - 1; i >= 0; i--) {
      const item = this.activeItems[i];

      // Handle active absorption into the runner
      if (item.userData.isAbsorbing) {
        item.userData.absorbProgress += dt * 7.0; // ~140ms absorption
        const p = Math.min(1.0, item.userData.absorbProgress);

        if (runnerPos) {
          // Rapidly converge toward runner chest
          item.position.x += (runnerPos.x - item.position.x) * Math.min(1.0, dt * 24.0);
          item.position.y += (runnerPos.y - item.position.y) * Math.min(1.0, dt * 24.0);
          item.position.z += (runnerPos.z - item.position.z) * Math.min(1.0, dt * 24.0);
        }

        // Shrink to zero
        const s = Math.max(0.01, 1.0 - p);
        item.scale.set(s, s, s);

        if (p >= 1.0) {
          this.releaseToPool(item);
          this.activeItems.splice(i, 1);
        }
        continue;
      }

      item.userData.lastZ = item.position.z;
      item.position.z += dz;

      // Rotate orbs for visual appeal
      if (item.userData.type === ObstacleType.ORB) {
        item.rotation.y += dt * 3.0;
      }

      // Recycle when past camera
      if (item.position.z > 20.0) {
        this.releaseToPool(item);
        this.activeItems.splice(i, 1);
      }
    }
  }

  getActiveItems() {
    return this.activeItems;
  }

  dispose() {
    this.reset();
    this.blockerGeo.dispose();
    this.hurdleGeo.dispose();
    this.orbGeo.dispose();
    this.orbRingGeo.dispose();

    this.blockerMaterial.dispose();
    this.hurdleMaterial.dispose();
    this.orbMaterial.dispose();
    this.orbRingMaterial.dispose();
  }
}
